'use client';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { PlusIcon } from 'lucide-react';
import LoadingDots from '../loading-dots';
import { useVariantStore } from '../useVariantStore';
import { useCartStore } from '../useCartStore';
import MotionMessage from '@/components/ui/MotionMessage';
import { Cart, CartItem } from '../../../lib/ecwid/ecwidTypes';

export const AddProductToCart = ({
    translation,
}: {
    translation: Record<string, string>;
}) => {
    const { product: fullProduct, selectedVariant } = useVariantStore();
    const { setCart, setCartOpen } = useCartStore(state => ({
        setCart: state.setCart,
        setCartOpen: state.setCartOpen,
    }));

    const [pending, setPending] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    if (!fullProduct) {
        return null;
    }

    const hasVariant = fullProduct.combinations.length;
    const isEnabled = !hasVariant || selectedVariant !== undefined;

    let inStock = true;

    if (selectedVariant) {
        inStock = selectedVariant.inStock;
    } else {
        inStock = fullProduct.inStock;
    }

    const handleAddToCart = () => {
        if (!isEnabled) {
            setMessage(translation.noProductToAdd);
            return;
        }

        if (!inStock) {
            setMessage(translation.outOfStock);
            return;
        }
        setPending(true);
        setMessage(null);

        if (
            typeof window !== 'undefined' &&
            window.Ecwid &&
            window.Ecwid.Cart
        ) {
            const options = selectedVariant?.options?.reduce(
                (acc, option) => {
                    acc[option.name] = option.value;
                    return acc;
                },
                {} as Record<string, string>,
            );

            window.Ecwid.Cart.addProduct({
                id: fullProduct.id,
                quantity: 1,
                options: selectedVariant ? options : undefined,
                callback: function (success, product, cart) {
                    if (success) {
                        setMessage(
                            translation.itemAdded ||
                                'Item added to cart successfully.',
                        );
                        window.Ecwid.Cart.get(() => {
                            const cartInfo: Cart = {
                                ...cart,
                                items: cart.items.map(
                                    (item: CartItem, index) => ({
                                        ...item,
                                        index,
                                        product: {
                                            id: item.product.id,
                                            sku: item.product.sku,
                                            price: item.product.price,
                                            name: item.product.name,
                                            isCustomerSetPrice:
                                                item.product
                                                    .isCustomerSetPrice ||
                                                false,
                                            weight: item.product.weight || 0,
                                            shortDescription:
                                                item.product.shortDescription ||
                                                '',
                                            url: item.product.url || '',
                                            mediaItem: {
                                                type: 'PICTURE',
                                                id:
                                                    item.product.mediaItem
                                                        ?.id || '',
                                                isMain:
                                                    item.product.mediaItem
                                                        ?.isMain || false,
                                                width:
                                                    item.product.mediaItem
                                                        ?.width || 0,
                                                height:
                                                    item.product.mediaItem
                                                        ?.height || 0,
                                                image160pxUrl:
                                                    item.product.mediaItem
                                                        ?.image160pxUrl || '',
                                                image400pxUrl:
                                                    item.product.mediaItem
                                                        ?.image400pxUrl || '',
                                                image800pxUrl:
                                                    item.product.mediaItem
                                                        ?.image800pxUrl || '',
                                                borderInfo: {
                                                    dominatingColor: {
                                                        isDark:
                                                            item.product
                                                                .mediaItem
                                                                ?.borderInfo
                                                                ?.dominatingColor
                                                                .isDark ||
                                                            false,
                                                        isFullyTransparent:
                                                            item.product
                                                                .mediaItem
                                                                ?.borderInfo
                                                                ?.dominatingColor
                                                                .isFullyTransparent ||
                                                            false,
                                                        rgbHexValue:
                                                            item.product
                                                                .mediaItem
                                                                ?.borderInfo
                                                                ?.dominatingColor
                                                                .rgbHexValue ||
                                                            '#ffffff',
                                                    },
                                                    homogeneity:
                                                        item.product.mediaItem
                                                            ?.borderInfo
                                                            ?.homogeneity ||
                                                        false,
                                                },
                                            },
                                        },
                                    }),
                                ),
                            };

                            setCart(cartInfo);
                            setCartOpen(true);
                        });
                    } else {
                        setMessage(
                            translation.addToCartError ||
                                'Failed to add item to cart.',
                        );
                    }
                    setPending(false);
                },
            });
        } else {
            setMessage(
                translation.addToCartError || 'Ecwid API is not available.',
            );
            setPending(false);
        }
    };

    return (
        <div className="relative w-full">
            <button
                aria-disabled={!isEnabled}
                className={clsx(
                    'relative flex w-full items-center justify-center rounded-full bg-primary-accent p-4 tracking-wide text-white',
                    { 'cursor-not-allowed opacity-60': !isEnabled },
                )}
                disabled={!isEnabled}
                onClick={handleAddToCart}
            >
                <div className="absolute left-0 ml-4">
                    {pending ? (
                        <LoadingDots className="mb-3 bg-white" />
                    ) : (
                        <PlusIcon className="h-5" />
                    )}
                </div>
                {translation.addToCart}
            </button>

            {message && <MotionMessage message={message} position="below" />}
        </div>
    );
};
